import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { render } from 'react-dom';
import 'mapbox-gl/dist/mapbox-gl.css';
import { lineLayer, fillLayer, circleLayer, symbolLayer } from "./layers"; // layers.js をインポート
import CourseMap from './course_map.js';

// -----Map.gl --------------------------------
import MapGL, {
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Source,
  Marker,
  FlyToInterpolator,
  Layer,
} from 'react-map-gl';

import Online from './online.js';
import Sups from '../marker/sups.js';

const TOKEN = 'pk.eyJ1IjoiaTAxMjEwbWwiLCJhIjoiY2t1NWZ0ZGNhNHp1eTJ2cDMxNXV0OHAwNCJ9.SqRPyXL7O65p-osCqqmp4Q'; // Set your mapbox token here
const geolocateStyle = {
  top: 0,
  left: 0,
  padding: '10px'
};
const fullscreenControlStyle = {
  top: 36,
  left: 0,
  padding: '10px'
};
const navStyle = {
  top: 72,
  left: 0,
  padding: '10px'
};
const scaleControlStyle = {
  bottom: 36,
  left: 0,
  padding: '10px'
};

// Map 本体
function MapView(props) {
  const mapRef = useRef();

  // Props
  const { center, style, online, height, setStyle, setLat, setLon, devices, setAccuracy, useWs, geoJson, isGlocal, lang } = props;
  const [mapLoaded, setMapLoaded] = useState(false);

  const [userLocation, setUserLocation] = useState(null);
  const [destinationPoint, setDestinationPoint] = useState(null);
  const [parkingPoints, setParkingPoints] = useState([]); // 駐車場（複数対応）
  const [coinParkings, setCoinParkings] = useState([]); // コインパーキング
  const [routeLine, setRouteLine] = useState(null);
  const [showStyleMenu, setShowStyleMenu] = useState(false);

  const isEnglish = lang && lang.includes('en');

  const mapStyles = isEnglish ? [
    { name: 'Streets', url: 'mapbox://styles/mapbox/streets-v11' },
    { name: 'Light', url: 'mapbox://styles/mapbox/light-v10' },
    { name: 'Satellite Streets', url: 'mapbox://styles/mapbox/satellite-streets-v11' }
  ] : [
    { name: '道路地図', url: 'mapbox://styles/mapbox/streets-v11' },
    { name: '明るい地図', url: 'mapbox://styles/mapbox/light-v10' },
    { name: '衛星写真＋道路', url: 'mapbox://styles/mapbox/satellite-streets-v11' }
  ];

  const handleStyleChange = (url) => {
    setStyle(url);
    setShowStyleMenu(false);
  };

  // State
  //---- normal react-map-gl -----------------
  const [viewport, setViewport] = useState({
    //latitude: center[0],
    //longitude: center[1],
    latitude: 35.28167445342043,
    longitude: 139.67306039427112,
    zoom: 3,
    //pitch: 30,
  });
  //---- normal react-map-gl ------------------

  // アイコンをロードする処理 (共通化)
  const reloadAllImages = () => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();
    if (!map) return;

    // location-icon
    map.loadImage('/location.png', (error, image) => {
      if (error) {
        console.error("Image loading error:", error);
      } else {
        if (!map.hasImage('location-icon')) {
          map.addImage('location-icon', image);
        }
      }
    });

    // GeoJSON icons
    if (geoJson) {
      geoJson.features.forEach((feature) => {
        const iconUrl = feature.properties["icon-image-url"];
        if (iconUrl && !map.hasImage(iconUrl)) {
          map.loadImage(iconUrl, (error, image) => {
            if (!error && image) {
              if (!map.hasImage(iconUrl)) map.addImage(iconUrl, image);
            }
          });
        }
      });
    }
  };

  //アイコンをロードする処理
  const handleMapLoad = () => {
    setMapLoaded(true);
    reloadAllImages();
  };

  //Glocal geofence
  useEffect(() => {
    if (!geoJson) return;

    const destFeature = geoJson.features.find(
      f => f.properties && f.properties.destination === true
    );

    if (destFeature && destFeature.geometry.type === "Point") {
      setDestinationPoint(destFeature.geometry.coordinates);
    }

    // Parking lot を検索 (name に "parking lot" を含むもの、大文字小文字区別なし)
    const parkingFeatures = geoJson.features.filter(
      f => f.properties && f.properties.name &&
        f.properties.name.toLowerCase().includes("parking lot") &&
        !f.properties.name.toLowerCase().includes("coin parking") &&
        f.geometry.type === "Point"
    );
    if (parkingFeatures.length > 0) {
      const points = parkingFeatures.map(f => ({
        name: f.properties.name,
        coordinates: f.geometry.coordinates
      }));
      setParkingPoints(points);
    } else {
      setParkingPoints([]);
    }

    // Coin parking を検索 (name に "coin parking" を含むもの)
    const coinParkingFeatures = geoJson.features.filter(
      f => f.properties && f.properties.name && f.properties.name.toLowerCase().includes("coin parking")
    );
    if (coinParkingFeatures.length > 0) {
      const points = coinParkingFeatures.map(f => ({
        name: f.properties.name,
        coordinates: f.geometry.coordinates
      }));
      setCoinParkings(points);
    }

    // geoJsonが変わったときも画像をロード
    reloadAllImages();

  }, [geoJson]);

  // 地図スタイル変更時に画像を再ロード
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const map = mapRef.current.getMap();

    const onStyleData = () => {
      reloadAllImages();
    };

    map.on('styledata', onStyleData);

    return () => {
      map.off('styledata', onStyleData);
    };
  }, [mapLoaded, geoJson]); // geoJsonが変わったときもリスナー内のクロージャ最新化のために依存に追加



  // Heat Center
  useEffect(() => {
    if (center[0] === 35.28167445342043 && center[1] === 139.67306039427112) return;
    setViewport({
      ...viewport,
      latitude: center[0],
      longitude: center[1],
      zoom: 15,
      transitionInterpolator: new FlyToInterpolator({ speed: 1.5 }),
      transitionDuration: 'auto',
    });
  }, [center]);

  // 目的地までのライン
  useEffect(() => {
    if (!userLocation || !destinationPoint) return;
    const line = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          userLocation,
          destinationPoint
        ]
      },
      properties: {}
    };
    setRouteLine(line);
  }, [userLocation, destinationPoint]);

  //目的地まで移動する
  const flyToDestination = () => {
    if (!destinationPoint) return;

    setViewport({
      ...viewport,
      longitude: destinationPoint[0],
      latitude: destinationPoint[1],
      zoom: 17,
      transitionInterpolator: new FlyToInterpolator({ speed: 1.5 }),
      transitionDuration: 1000, // ← 1秒
    });
  };



  let onViewportChange = function (arg) {
    setViewport(arg)
  }

  // ユーザーの位置情報を取得した際に呼び出される関数
  const onUserLocationUpdate = (event) => {
    const { coords } = event;
    console.log('Current Latitude:', coords.latitude);
    console.log('Current Longitude:', coords.longitude);
    console.log('Current accuracy:', coords.accuracy);
    // 現在位置をセットするなどの処理を行うことができます
    setLat(coords.latitude)
    setLon(coords.longitude)
    setAccuracy(coords.accuracy)
    setUserLocation([coords.longitude, coords.latitude]);

  };

  const trail = (
    <Source
      id="gsi-tiles"
      type="raster"
      tiles={['https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png']}
      tileSize={256}
    >
      <Layer id="gsi-layer" type="raster" minZoom={5} maxZoom={18} />
    </Source>
  );

  //HOMEボタンスタイル
  const styles = {
    roomButton: {
      position: "absolute",
      top: "10px",
      right: "15px",
      background: "rgba(255,255,255,0.85)",
      backdropFilter: "blur(4px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderRadius: "24px",
      padding: "8px 14px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
      transition: "0.2s ease",
      zIndex: 999,
    },
    parkingButton: { // 駐車場のボタンスタイル
      position: "absolute",
      top: "60px", // Your roomの下
      right: "15px",
      background: "rgba(255,255,255,0.85)",
      backdropFilter: "blur(4px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderRadius: "24px",
      padding: "8px 14px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
      transition: "0.2s ease",
      zIndex: 999,
    },
    parkingButtonDisabled: { // 駐不可のボタンスタイル
      position: "absolute",
      top: "60px",
      right: "15px",
      background: "rgba(240,240,240,0.85)", // グレー背景
      backdropFilter: "blur(4px)",
      boxShadow: "none",
      borderRadius: "24px",
      padding: "8px 14px",
      display: "flex",
      alignItems: "center",
      cursor: "not-allowed", // 禁止カーソル
      userSelect: "none",
      transition: "0.2s ease",
      zIndex: 999,
      color: "#999", // 文字色グレー
    },
    roomIcon: {
      fontSize: "20px",
      marginRight: "6px",
    },
    roomText: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#333",
    },
    styleButton: {
      position: "absolute",
      bottom: "30px", // 左下のスケールコントロールなどと被らないように調整
      right: "15px",
      background: "rgba(255,255,255,0.85)",
      backdropFilter: "blur(4px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderRadius: "24px",
      padding: "8px 14px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
      transition: "0.2s ease",
      zIndex: 999,
    },
    styleMenu: {
      position: "absolute",
      bottom: "80px", // ボタンの上に表示
      right: "15px",
      background: "rgba(255,255,255,0.95)",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      padding: "10px",
      zIndex: 999,
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    styleOption: {
      cursor: "pointer",
      padding: "5px 10px",
      borderRadius: "4px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#333",
    },
  };

  //Geojsonのポイントをクリックした場合
  const [popupInfo, setPopupInfo] = useState(null);
  const handleClickGeojson = (event) => {
    const feature = event.features && event.features[0];
    if (feature && feature.properties["image-url"]) {
      setPopupInfo({
        longitude: event.lngLat[0],
        latitude: event.lngLat[1],
        imageUrl: feature.properties["image-url"],
        width: feature.properties["image-width"] || 200,   // デフォルト200
        height: feature.properties["image-height"] || 200, // デフォルト200
      });
    }
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* 🏠 目的地ジャンプボタン */}
      {isGlocal && destinationPoint && (
        <div style={styles.roomButton} onClick={flyToDestination}>
          <span style={styles.roomIcon}>🏠</span>
          <span style={styles.roomText}>Your room</span>
        </div>
      )}

      {/* 🅿️ 駐車場ジャンプボタン (GLOCALモードのみ) */}
      {isGlocal && (
        parkingPoints.length > 0 ? (
          parkingPoints.map((pp, index) => (
            <div
              key={`parking-${index}`}
              style={{
                ...styles.parkingButton,
                top: `${60 + (index * 50)}px`
              }}
              onClick={() => {
                setViewport({
                  ...viewport,
                  longitude: pp.coordinates[0],
                  latitude: pp.coordinates[1],
                  zoom: 19,
                  transitionInterpolator: new FlyToInterpolator({ speed: 1.5 }),
                  transitionDuration: 1000,
                });
              }}
            >
              <span style={styles.roomIcon}>🅿️</span>
              <span style={styles.roomText}>{pp.name}</span>
            </div>
          ))
        ) : (
          <div style={styles.parkingButtonDisabled}>
            <span style={styles.roomIcon}>🅿️</span>
            <span style={styles.roomText}>No Parking</span>
          </div>
        )
      )}

      {/* 🅿️ Coin Parking ジャンプボタン (GLOCALモードのみ) */}
      {isGlocal && coinParkings.map((cp, index) => (
        <div
          key={index}
          style={{
            ...styles.parkingButton,
            top: `${60 + ((parkingPoints.length > 0 ? parkingPoints.length : 1) * 50) + (index * 50)}px` // Parking lot の数に応じて動的に調整
          }}
          onClick={() => {
            setViewport({
              ...viewport,
              longitude: cp.coordinates[0],
              latitude: cp.coordinates[1],
              zoom: 19,
              transitionInterpolator: new FlyToInterpolator({ speed: 1.5 }),
              transitionDuration: 1000,
            });
          }}
        >
          <span style={styles.roomIcon}>🅿️</span>
          <span style={styles.roomText}>coin parking {index + 1}</span>
        </div>
      ))}

      {isGlocal && (
        <>
          <div style={styles.styleButton} onClick={() => setShowStyleMenu(!showStyleMenu)}>
            <span style={styles.roomIcon}>🗺️</span>
            <span style={styles.roomText}>{isEnglish ? "Map Style" : "地図切替"}</span>
          </div>
          {showStyleMenu && (
            <div style={styles.styleMenu}>
              {mapStyles.map((s) => (
                <div
                  key={s.name}
                  style={{
                    ...styles.styleOption,
                    backgroundColor: style === s.url ? "#e0e0e0" : "transparent"
                  }}
                  onClick={() => handleStyleChange(s.url)}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <MapGL
        ref={mapRef}
        mapStyle={style}
        {...viewport}
        width="100%"
        height={height}
        onViewportChange={onViewportChange}
        mapboxApiAccessToken={TOKEN}
        attributionControl={false}
        onLoad={handleMapLoad}
        onClick={handleClickGeojson} // クリックイベントを追加
      >

        {geoJson && (
          <>
            <CourseMap courseGeojson={geoJson} />
            <Source id="geofence-source" type="geojson" data={geoJson}>
              <Layer {...fillLayer} />
              <Layer {...circleLayer} />
              <Layer {...symbolLayer} />
            </Source>
          </>
        )}

        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            closeButton={true}
            closeOnClick={false}
            onClose={() => setPopupInfo(null)}
          >
            <img
              src={popupInfo.imageUrl}
              alt="popup"
              width={popupInfo.width}
              height={popupInfo.height}
            />
          </Popup>
        )}

        {routeLine && (//目的地までのライン
          <Source id="route-line" type="geojson" data={routeLine}>
            <Layer
              id="route-line-layer"
              type="line"
              paint={{
                'line-color': '#FF3B30',
                'line-width': 2,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}
        <Sups devices={devices} />

        <GeolocateControl auto={true} // 初期表示時に有効にする
          positionOptions={{ enableHighAccuracy: true }}
          showUserHeading={true}
          showUserLocation={true}
          onGeolocate={onUserLocationUpdate}
          style={geolocateStyle}
          trackUserLocation={true} />
        <NavigationControl style={navStyle} />
      </MapGL>

      {useWs &&
        <Online online={online} />
      }
    </div>
  );
}
export default React.memo(MapView);
